/**
 * "Show last readings", for THIS page view only — and the day it moves the page to.
 *
 * It used to be remembered per site for the session (`readRevealed` / `writeRevealed` in
 * `lib/site-liveness.ts`), and that hid the not-reporting screen on every later visit: Hamzah
 * opened the Overview after pressing it once earlier and read the screen as gone (2026-10-13).
 * A silent site must greet every visit with the screen; the old readings are one press away.
 * So the choice lives in memory, and a different site (the header changed) or a new visit starts
 * from the screen again.
 *
 * ── AND IT NOW SHOWS THE READINGS (Hamzah, 2026-10-14) ───────────────────────────────────
 * Pressing it gave the page back as it is NOW: "Power today" empty, "– kWh today", every plant
 * "–", because a site silent since Oct 9 has nothing today. The choice now also moves the page's
 * DATA CLOCK (`lib/data-clock.tsx`) to the last reading, so every "today" the page computes is the
 * last day with readings. That is why the state lives in `SilenceRevealProvider`, around the page in
 * `App.tsx` (keyed on the tab, so another page starts afresh), and not in the page: the clock has
 * to be in place ABOVE the page's own hooks — the Overview reads today's counters in its body.
 *
 * Callers pass the subject's liveness to have the clock moved; without it (the Digital Twin, which
 * has its own day player) the choice is remembered and the clock left alone.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { PastClock } from '@/lib/data-clock';
import { lastDataDay, lastReadingClock, whenWords, type Liveness } from '@/lib/site-liveness';

export interface Revealed {
  key: string;
  /** The data clock the page reads while revealed; null leaves it live. */
  clock: PastClock | null;
}

/** Provided by `SilenceRevealProvider`. */
export const RevealContext = createContext<{
  revealed: Revealed | null;
  set: (next: Revealed | null) => void;
} | null>(null);

export function useSilenceReveal(
  key: string | null | undefined,
  liveness?: Liveness,
  /** The site's own zone, for the day's words ("Oct 9"); the viewer's when absent. */
  timeZone?: string,
): [boolean, (on: boolean) => void] {
  const ctx = useContext(RevealContext);
  /* Outside a provider (a test, a page rendered on its own) the choice is the component's own. */
  const [local, setLocal] = useState(false);
  const revealed = ctx ? ctx.revealed !== null && ctx.revealed.key === key : local;
  const atMs = liveness ? lastReadingClock(liveness) : null;
  const day = liveness ? (lastDataDay(liveness, timeZone)?.label ?? null) : null;

  const set = useCallback(
    (on: boolean) => {
      if (!ctx) {
        setLocal(on);
        return;
      }
      const clock: PastClock | null =
        atMs !== null && day !== null
          ? { atMs, day, at: whenWords(atMs - 60_000, 0, timeZone) }
          : null;
      ctx.set(on && key ? { key, clock } : null);
    },
    [ctx, key, atMs, day, timeZone],
  );

  /*
   * A different site starts from the screen — when the KEY CHANGES (the header moved), never on
   * mount: a second caller mounting with its own key (a device page inside a site's hub) must not
   * wipe a choice another caller made.
   */
  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    if (!ctx) setLocal(false);
    else if (ctx.revealed && ctx.revealed.key !== key) ctx.set(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [revealed, set];
}
